/**
 * CharacterLoraWizard — the create flow opened by "+ new character LoRA".
 * Three stages mirroring the proven manual pipeline:
 *   1. Source  — name + body spec + upload the canonical character image
 *   2. Curate  — the generated set, grouped Face/Body/Variety; select / delete / regen
 *   3. Train   — config + launch (klein-9B, ai-toolkit, A100)
 *
 * Mock data for now; the stage transitions + curation are wired so the flow is real
 * to click through. Wire to /admin/loras (services/api/loras) when the backend lands.
 */
import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CloudUpload,
  Cpu,
  Images,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
  User,
  PersonStanding,
  Image as ImageIcon,
} from 'lucide-react';

import { mockFrames, type Frame, type FrameGroup } from '@/services/api/loras.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

type Stage = 'source' | 'curate' | 'train';

const STAGES: { key: Stage; label: string }[] = [
  { key: 'source', label: 'Source' },
  { key: 'curate', label: 'Curate' },
  { key: 'train', label: 'Train' },
];

const GROUP_ICON: Record<FrameGroup, typeof User> = {
  face: User,
  body: PersonStanding,
  variety: ImageIcon,
};

function SpecField({ label, options, value }: { label: string; options: string[]; value: string }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select defaultValue={value}>
        <SelectTrigger className="mt-1 h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function CharacterLoraWizard({ onBack }: { onBack: () => void }) {
  const [stage, setStage] = useState<Stage>('source');
  const [group, setGroup] = useState<FrameGroup>('face');
  const [frames, setFrames] = useState<Frame[]>(() => mockFrames());
  const [name, setName] = useState('LIN');

  const counts = useMemo(() => {
    const by = (g: FrameGroup) => frames.filter((f) => f.group === g);
    const sel = (g: FrameGroup) => by(g).filter((f) => f.selected).length;
    return {
      face: { total: by('face').length, sel: sel('face') },
      body: { total: by('body').length, sel: sel('body') },
      variety: { total: by('variety').length, sel: sel('variety') },
      totalSel: frames.filter((f) => f.selected).length,
      total: frames.length,
    };
  }, [frames]);

  const toggle = (id: string) =>
    setFrames((fs) => fs.map((f) => (f.id === id ? { ...f, selected: !f.selected } : f)));
  const remove = (id: string) => setFrames((fs) => fs.filter((f) => f.id !== id));

  const visible = frames.filter((f) => f.group === group);
  const Icon = GROUP_ICON[group];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> All LoRAs
        </Button>
        <span className="text-sm text-muted-foreground">/ New character LoRA</span>
      </div>

      <div className="flex items-center gap-2">
        {STAGES.map((s, i) => {
          const active = s.key === stage;
          const done = STAGES.findIndex((x) => x.key === stage) > i;
          return (
            <div key={s.key} className="flex flex-1 items-center gap-2">
              <button
                type="button"
                onClick={() => setStage(s.key)}
                className={cn(
                  'flex items-center gap-2 text-sm font-medium',
                  active ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border text-xs',
                    active && 'border-indigo-500 text-indigo-600',
                    done && 'border-emerald-500 text-emerald-600',
                  )}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                {s.label}
              </button>
              {i < STAGES.length - 1 && <span className="h-px flex-1 bg-border" />}
            </div>
          );
        })}
      </div>

      {stage === 'source' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div>
              <Label htmlFor="lora-name">Character name</Label>
              <Input
                id="lora-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Body spec</Label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <SpecField label="Build" value="slim" options={['slim', 'athletic', 'curvy', 'petite']} />
                <SpecField label="Breast" value="C-cup" options={['A-cup', 'B-cup', 'C-cup', 'D-cup']} />
                <SpecField label="Skin" value="fair" options={['fair', 'light', 'olive', 'tan']} />
                <SpecField label="Pubic hair" value="trimmed" options={['shaved', 'trimmed', 'natural', 'full']} />
                <SpecField label="Labia" value="T4" options={['T1', 'T3', 'T4']} />
              </div>
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Canonical image</Label>
            <div className="flex h-[180px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-muted-foreground">
              <CloudUpload className="h-7 w-7" aria-hidden />
              <span className="text-sm">drop the character headshot</span>
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <Check className="h-3 w-3" /> headshot-LIN.png
              </span>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button onClick={() => setStage('curate')} className="gap-1.5">
                <Sparkles className="h-4 w-4" /> Generate training set
              </Button>
              <span className="text-xs text-muted-foreground">~44 imgs · 24 face · 12 body · 8 variety</span>
            </div>
          </div>
        </div>
      )}

      {stage === 'curate' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {(['face', 'body', 'variety'] as FrameGroup[]).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroup(g)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm font-medium capitalize transition-colors',
                  group === g
                    ? 'border-border bg-accent text-accent-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {g}{' '}
                <span className="text-xs text-muted-foreground">
                  {counts[g].sel}/{counts[g].total}
                </span>
              </button>
            ))}
            <span className="ml-auto text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{counts.totalSel}</span> of {counts.total} selected
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate selected
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Trash2 className="h-3.5 w-3.5" /> Delete unselected
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> Add variety
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {visible.map((f) => (
              <div
                key={f.id}
                className={cn(
                  'group relative overflow-hidden rounded-md border transition-opacity',
                  f.selected ? 'border-indigo-500' : 'border-border opacity-40',
                )}
              >
                <button
                  type="button"
                  onClick={() => toggle(f.id)}
                  aria-label={f.selected ? 'deselect' : 'select'}
                  className={cn(
                    'absolute left-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded border',
                    f.selected ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-white/80 bg-black/30 text-transparent',
                  )}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <div className="absolute right-1.5 top-1.5 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button type="button" aria-label="regenerate" className="flex h-6 w-6 items-center justify-center rounded bg-black/55 text-white">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => remove(f.id)} aria-label="delete" className="flex h-6 w-6 items-center justify-center rounded bg-black/55 text-white">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button type="button" onClick={() => toggle(f.id)} className="block w-full">
                  <div className="flex h-20 items-center justify-center bg-muted text-muted-foreground">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="truncate px-2 py-1.5 text-left text-xs text-muted-foreground">{f.label}</div>
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <Button onClick={() => setStage('train')} className="gap-1.5">
              Continue to train <Cpu className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {stage === 'train' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-1">
            {[
              ['Base model', 'FLUX.2 klein-9B'],
              ['Trigger word', name.toLowerCase().replace(/[^a-z0-9]/g, '') + 'char'],
              ['Rank / alpha', '64 / 64'],
              ['Steps', '3000'],
              ['Resolution', '≤ 1280'],
              ['Dataset', `${counts.totalSel} images`],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b py-2.5 text-sm last:border-0">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
            <Button className="mt-3 gap-1.5">
              <Play className="h-4 w-4" /> Start training
            </Button>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-medium">
              <Cpu className="h-4 w-4 text-indigo-500" /> Compute
            </p>
            <div className="space-y-1.5 text-sm text-muted-foreground">
              <p>Trains on the A100 (cuda:3, shared) via ai-toolkit.</p>
              <p className="flex items-center gap-1.5">
                <Images className="h-3.5 w-3.5" /> {counts.totalSel} curated images
              </p>
              <p>Checkpoints + samples every 500 steps — pick the pre-overfit best.</p>
              <p>Estimated ~2–3 hrs. You'll get a notification when it's ready.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
